/* File: PublicAlbum.jsx
 * Path: ananunadm/src/pages/PublicAlbum/PublicAlbum.jsx
 *
 * Public, unauthenticated viewer for an album's public share link (e.g. a
 * person's photos shared from the People page). Mirrors PublicKnowledgeArticle
 * / PublicCareers: raw axios against /public/*, no private-token header.
*/
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Alert, Box, Button, Container, Group, PasswordInput, Text, Title } from "@mantine/core";
import { IconLock } from "@tabler/icons-react";
import config from "../../config";
import Loading from "../../helpers/loading";
import MediaLightbox from "../../components/MediaLightbox";
import OrganizedMediaGallery from "../../components/media/gallery/OrganizedMediaGallery";
import { getUploadAssetSrc, getUploadDisplaySrc } from "../../helpers/uploadImage";
import styles from "../../components/media/gallery/places.module.css";

const apiBase = `${config.api.API_URL}/${config.api.API_Version}/public/album`;

const toPhoto = (upload, index) => {
	if (!upload?.path) return null;
	const width = Number(upload.width) || 800;
	const height = Number(upload.height) || 600;
	const title = upload.name || upload.alt || `Photo ${index + 1}`;
	return {
		id: upload._id || `${upload.path}-${index}`,
		src: getUploadDisplaySrc(upload, 320),
		fallbackSrc: getUploadAssetSrc(upload, upload.path || upload.pathThumb),
		originalSrc: getUploadDisplaySrc(upload, 1920),
		width,
		height,
		title,
		alt: upload.alt || title,
		date: new Date(upload.date || upload.createdAt || 0)
	};
};

export default function PublicAlbum() {
	const { token } = useParams();
	const [meta, setMeta] = useState(null);
	const [notFound, setNotFound] = useState(false);
	const [password, setPassword] = useState("");
	const [passwordError, setPasswordError] = useState("");
	const [unlocking, setUnlocking] = useState(false);
	const [photos, setPhotos] = useState(null);
	const [lightboxIndex, setLightboxIndex] = useState(-1);

	const loadAlbum = useCallback((pwd) => {
		setUnlocking(true);
		setPasswordError("");
		axios.post(`${apiBase}/${encodeURIComponent(token)}`, pwd ? { password: pwd } : {})
			.then((response) => {
				const uploads = Array.isArray(response.data?.photos) ? response.data.photos : [];
				setPhotos(uploads.map(toPhoto).filter(Boolean));
			})
			.catch((requestError) => {
				if (requestError?.response?.status === 401) {
					setPasswordError(requestError.response.data?.error || "Incorrect password");
				} else {
					setNotFound(true);
				}
			})
			.finally(() => setUnlocking(false));
	}, [token]);

	useEffect(() => {
		let mounted = true;
		axios.get(`${apiBase}/${encodeURIComponent(token)}`)
			.then((response) => {
				if (!mounted) return;
				setMeta(response.data);
				if (!response.data?.requiresPassword) loadAlbum();
			})
			.catch(() => {
				if (mounted) setNotFound(true);
			});
		return () => { mounted = false; };
	}, [token, loadAlbum]);

	const submitPassword = (event) => {
		event.preventDefault();
		loadAlbum(password);
	};

	const handlePhotoClick = (index, photoList) => {
		const clicked = photoList[index];
		const fullIndex = (photos || []).findIndex((photo) => photo.id === clicked?.id);
		setLightboxIndex(fullIndex >= 0 ? fullIndex : 0);
	};

	if (notFound) {
		return (
			<Box mih="100vh" py={80}>
				<Container size="sm">
					<Title order={2}>Link unavailable</Title>
					<Text color="dimmed" mt="sm">This shared album link is not available or has expired.</Text>
				</Container>
			</Box>
		);
	}

	if (!meta) return <Loading />;

	if (meta.requiresPassword && !photos) {
		return (
			<Box mih="100vh" py={80}>
				<Container size="xs">
					<Title order={2} mb="xs">{meta.name || "Shared album"}</Title>
					<Text color="dimmed" mb="lg">This album is password protected.</Text>
					<form onSubmit={submitPassword}>
						<Group align="flex-end" noWrap>
							<PasswordInput
								icon={<IconLock size={16} />}
								placeholder="Password"
								value={password}
								onChange={(event) => setPassword(event.currentTarget.value)}
								style={{ flex: 1 }}
								autoFocus
							/>
							<Button type="submit" loading={unlocking}>View</Button>
						</Group>
						{passwordError && <Alert color="red" mt="sm">{passwordError}</Alert>}
					</form>
				</Container>
			</Box>
		);
	}

	const slides = (photos || []).map((photo) => ({ ...photo, src: photo.originalSrc || photo.src }));

	return (
		<Box mih="100vh" className={styles.page}>
			<Box className={styles.header}>
				<Title order={3}>{meta.name || "Shared album"}</Title>
				<Text size="sm" color="dimmed">
					{photos ? `${photos.length} ${photos.length === 1 ? "photo" : "photos"}` : "Loading…"}
				</Text>
			</Box>
			{!photos ? (
				<Loading />
			) : photos.length > 0 ? (
				<Box className={styles.photoGrid}>
					<OrganizedMediaGallery photos={photos} onPhotoClick={handlePhotoClick} />
				</Box>
			) : (
				<Container size="sm"><Alert color="yellow">This album has no photos yet.</Alert></Container>
			)}
			<MediaLightbox
				open={lightboxIndex >= 0}
				index={lightboxIndex}
				close={() => setLightboxIndex(-1)}
				slides={slides}
			/>
		</Box>
	);
}
